export type DataDictionaryColumn = {
  comment: string | null;
  foreignKey: {
    columns: string[];
    targetColumns: string[];
    targetTable: string;
  } | null;
  isPrimaryKey: boolean;
  name: string;
  nullable: boolean;
  type: string;
};

export type DataDictionaryTable = {
  carespacePath: {
    kind: "direct" | "indirect" | "none" | "root";
    steps: Array<{
      columns: string[];
      table: string;
      targetColumns: string[];
      targetTable: string;
    }>;
  };
  columns: DataDictionaryColumn[];
  comment: string | null;
  name: string;
};

export type DataDictionaryResponse = {
  catalog: {
    coverage: {
      columns: { documented: number; percent: number; total: number };
      tables: { documented: number; percent: number; total: number };
    };
    tables: DataDictionaryTable[];
  };
  embedToken: string;
  erdUrl: string;
  generatedAt: string;
  repository: string;
  schemaPath: string;
  sha: string;
  shortSha: string;
};

export function isDataDictionaryResponse(value: unknown): value is DataDictionaryResponse {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const body = value as Record<string, unknown>;
  return (
    typeof body.sha === "string" &&
    typeof body.shortSha === "string" &&
    typeof body.repository === "string" &&
    typeof body.schemaPath === "string" &&
    typeof body.generatedAt === "string" &&
    typeof body.erdUrl === "string" &&
    typeof body.embedToken === "string" &&
    body.embedToken.length > 0 &&
    isCatalog(body.catalog)
  );
}

function isCatalog(value: unknown): value is DataDictionaryResponse["catalog"] {
  if (typeof value !== "object" || value === null) return false;
  const catalog = value as Record<string, unknown>;
  return (
    Array.isArray(catalog.tables) && catalog.tables.every(isTable) && isCoverage(catalog.coverage)
  );
}

function isCoverage(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const coverage = value as Record<string, unknown>;
  return isCoverageMetric(coverage.tables) && isCoverageMetric(coverage.columns);
}

function isCoverageMetric(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const metric = value as Record<string, unknown>;
  return (
    typeof metric.documented === "number" &&
    typeof metric.percent === "number" &&
    typeof metric.total === "number"
  );
}

function isTable(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const table = value as Record<string, unknown>;
  return (
    typeof table.name === "string" &&
    (typeof table.comment === "string" || table.comment === null) &&
    Array.isArray(table.columns) &&
    table.columns.every(isColumn) &&
    isCarespacePath(table.carespacePath)
  );
}

function isColumn(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const column = value as Record<string, unknown>;
  return (
    typeof column.name === "string" &&
    typeof column.type === "string" &&
    typeof column.nullable === "boolean" &&
    typeof column.isPrimaryKey === "boolean" &&
    (typeof column.comment === "string" || column.comment === null) &&
    (column.foreignKey === null || isForeignKey(column.foreignKey))
  );
}

function isForeignKey(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const foreignKey = value as Record<string, unknown>;
  return (
    typeof foreignKey.targetTable === "string" &&
    isStringArray(foreignKey.columns) &&
    isStringArray(foreignKey.targetColumns)
  );
}

function isCarespacePath(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const path = value as Record<string, unknown>;
  return (
    (path.kind === "direct" ||
      path.kind === "indirect" ||
      path.kind === "none" ||
      path.kind === "root") &&
    Array.isArray(path.steps) &&
    path.steps.every(isJoinStep)
  );
}

function isJoinStep(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const step = value as Record<string, unknown>;
  return (
    typeof step.table === "string" &&
    typeof step.targetTable === "string" &&
    isStringArray(step.columns) &&
    isStringArray(step.targetColumns)
  );
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}
